// One usage limit = one message: an adjacent run of limit banners, notices, failed
// subagent lines and boundaries collapses to a single entry (the error card if any).
import type { StreamEntry } from "./state.svelte.js";

// Mirrors server/src/server/cyborg/rate-limit-banner.ts (ui cannot import server).
const BANNER_WINDOW_LABELS = ["session limit", "weekly limit", "opus limit", "sonnet limit"];

function isUsageLimitBannerText(text: string): boolean {
  const trimmed = text.trim().toLowerCase();
  if (!trimmed.startsWith("you've hit your ")) return false;
  if (!BANNER_WINDOW_LABELS.some((label) => trimmed.includes(label))) return false;
  return trimmed.includes("reset");
}

const NOTICE_TAILS = [
  "at its usage limit right now and will reply once it resets.",
  "I'm at my usage limit right now and will reply once it resets.",
  "at its usage limit right now. Ask again after it resets.", // a failed turn nothing replays
  "I'm at my usage limit right now. Ask again after it resets.",
];

function isUsageLimitNoticeText(text: string): boolean {
  const trimmed = text.trim();
  return NOTICE_TAILS.some((tail) => trimmed.endsWith(tail)); // may lead with "Paused until …: "
}

export function isUsageLimitText(text: string): boolean {
  return isUsageLimitBannerText(text) || isUsageLimitNoticeText(text);
}

function isLimitRow(entry: StreamEntry): boolean {
  if (entry.kind === "error") return isUsageLimitBannerText(entry.content);
  return entry.kind === "text" && isUsageLimitText(entry.content);
}

function isFailedTaskLine(entry: StreamEntry): boolean {
  return (
    entry.kind === "tool_call" && entry.name === "task_notification" && entry.status === "failed"
  );
}

function joinsLimitRun(entry: StreamEntry): boolean {
  return isLimitRow(entry) || isFailedTaskLine(entry) || entry.kind === "turn_boundary";
}

export function withoutUsageLimitRepeats(entries: readonly StreamEntry[]): readonly StreamEntry[] {
  if (!entries.some(isLimitRow)) return entries;
  const out: StreamEntry[] = [];
  let index = 0;
  while (index < entries.length) {
    if (!joinsLimitRun(entries[index])) {
      out.push(entries[index++]);
      continue;
    }
    let end = index;
    while (end < entries.length && joinsLimitRun(entries[end])) end++;
    const run = entries.slice(index, end);
    const limitRows = run.filter(isLimitRow);
    if (limitRows.length === 0) out.push(...run);
    else {
      const errors = limitRows.filter((e) => e.kind === "error");
      const kept = errors.at(-1) ?? limitRows.at(-1);
      out.push(
        ...run.filter((e) => e === kept || (e.kind === "turn_boundary" && e === run.at(-1))),
      );
    }
    index = end;
  }
  return out;
}
